import { useState, useEffect } from 'react';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { useSnackbar } from 'notistack';
import Sidebar from './Sidebar';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const emptyForm = { label: "Home", address: "", region: "", areaId: "", phoneNo: "", phoneCountryCode: "852", isDefault: false };

// A curated list, not exhaustive — covers the countries customers most
// commonly need for their WhatsApp/contact number. Delivery itself
// remains Hong Kong only regardless of which one is chosen here.
const COUNTRY_CODES = [
    { code: "852", label: "🇭🇰 +852 (Hong Kong)" },
    { code: "91", label: "🇮🇳 +91 (India)" },
    { code: "86", label: "🇨🇳 +86 (China)" },
    { code: "65", label: "🇸🇬 +65 (Singapore)" },
    { code: "60", label: "🇲🇾 +60 (Malaysia)" },
    { code: "44", label: "🇬🇧 +44 (UK)" },
    { code: "1", label: "🇺🇸 +1 (US/Canada)" },
    { code: "61", label: "🇦🇺 +61 (Australia)" },
    { code: "81", label: "🇯🇵 +81 (Japan)" },
    { code: "82", label: "🇰🇷 +82 (South Korea)" },
    { code: "971", label: "🇦🇪 +971 (UAE)" },
    { code: "92", label: "🇵🇰 +92 (Pakistan)" },
    { code: "880", label: "🇧🇩 +880 (Bangladesh)" },
    { code: "63", label: "🇵🇭 +63 (Philippines)" },
    { code: "62", label: "🇮🇩 +62 (Indonesia)" },
];

const ManageAddresses = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [addresses, setAddresses] = useState([]);
    const [shippingAreas, setShippingAreas] = useState([]);

    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);

    const fetchAll = async () => {
        try {
            const [addressesRes, areasRes] = await Promise.all([
                axios.get('/api/v1/addresses'),
                axios.get('/api/v1/shipping-areas'),
            ]);
            setAddresses(addressesRes.data.addresses);
            setShippingAreas(areasRes.data.areas);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to load addresses", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const regions = [...new Set(shippingAreas.map((a) => a.region))];
    const areasForRegion = shippingAreas.filter((a) => a.region === form.region);
    const selectedArea = shippingAreas.find((a) => a._id === form.areaId);

    const startAdd = () => {
        setForm(emptyForm);
        setEditingId(null);
        setShowForm(true);
    };

    const startEdit = (addr) => {
        setForm({ label: addr.label, address: addr.address, region: addr.region, areaId: addr.areaId, phoneNo: addr.phoneNo, phoneCountryCode: addr.phoneCountryCode || "852", isDefault: addr.isDefault });
        setEditingId(addr._id);
        setShowForm(true);
    };

    const submitHandler = async (e) => {
        e.preventDefault();
        if (form.phoneCountryCode === "852" && form.phoneNo.length !== 8) {
            enqueueSnackbar("Hong Kong phone numbers must be 8 digits", { variant: "warning" });
            return;
        }
        if (form.phoneCountryCode !== "852" && (form.phoneNo.length < 6 || form.phoneNo.length > 15)) {
            enqueueSnackbar("Please enter a valid phone number", { variant: "warning" });
            return;
        }
        if (!form.areaId) {
            enqueueSnackbar("Please choose a delivery area", { variant: "warning" });
            return;
        }

        const payload = { ...form, area: selectedArea?.name, country: "HK" };

        setSaving(true);
        try {
            if (editingId) {
                await axios.put(`/api/v1/address/${editingId}`, payload);
                enqueueSnackbar("Address updated", { variant: "success" });
            } else {
                await axios.post('/api/v1/address/new', payload);
                enqueueSnackbar("Address saved", { variant: "success" });
            }
            setShowForm(false);
            fetchAll();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to save address", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    const deleteHandler = async (id) => {
        if (!window.confirm("Delete this address?")) return;
        try {
            await axios.delete(`/api/v1/address/${id}`);
            enqueueSnackbar("Address deleted", { variant: "success" });
            setAddresses((prev) => prev.filter((a) => a._id !== id));
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to delete address", { variant: "error" });
        }
    };

    const makeDefaultHandler = async (addr) => {
        try {
            await axios.put(`/api/v1/address/${addr._id}`, { ...addr, isDefault: true });
            enqueueSnackbar("Default address updated", { variant: "success" });
            fetchAll();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to update", { variant: "error" });
        }
    };

    return (
        <>
            <MetaData title="Manage Addresses | 1313 Mart" />
            {(loading || saving) && <BackdropLoader />}

            <main className="w-full mt-12 sm:mt-0">
                <div className="flex flex-col sm:flex-row gap-3.5 w-full sm:w-11/12 mt-0 sm:mt-4 m-auto sm:mb-7">

                    <Sidebar activeTab="addresses" />

                    <div className="flex-1 bg-white rounded-sm shadow p-4 sm:p-6 flex flex-col gap-4">
                        <div className="flex justify-between items-center">
                            <h1 className="text-lg font-medium">Manage Addresses</h1>
                            {!showForm && (
                                <button onClick={startAdd} className="bg-primary-orange text-white text-sm font-medium px-4 py-2 rounded shadow hover:shadow-lg">
                                    + Add New Address
                                </button>
                            )}
                        </div>

                        {showForm && (
                            <form onSubmit={submitHandler} className="flex flex-col gap-3 border rounded p-4">
                                <TextField label="Label (e.g. Home, Work)" size="small" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
                                <TextField label="Address (building, street, floor, etc.)" size="small" required fullWidth value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />

                                <div className="flex gap-3">
                                    <TextField
                                        label="Region"
                                        select
                                        size="small"
                                        fullWidth
                                        required
                                        value={form.region}
                                        onChange={(e) => setForm({ ...form, region: e.target.value, areaId: "" })}
                                    >
                                        {regions.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                                    </TextField>
                                    <TextField
                                        label="Area"
                                        select
                                        size="small"
                                        fullWidth
                                        required
                                        disabled={!form.region}
                                        value={form.areaId}
                                        onChange={(e) => setForm({ ...form, areaId: e.target.value })}
                                    >
                                        {areasForRegion.map((a) => <MenuItem key={a._id} value={a._id}>{a.name}</MenuItem>)}
                                    </TextField>
                                </div>

                                {selectedArea && (
                                    <div className="bg-blue-50 rounded p-3 text-sm text-gray-700">
                                        <p>Delivery days: {selectedArea.deliveryDays || "—"}</p>
                                        <p>Free shipping on orders over HK${selectedArea.minOrderValue} (otherwise HK${selectedArea.shippingCharge} shipping)</p>
                                    </div>
                                )}

                                <div className="flex gap-2">
                                    <TextField
                                        select
                                        label="Code"
                                        size="small"
                                        value={form.phoneCountryCode}
                                        onChange={(e) => setForm({ ...form, phoneCountryCode: e.target.value })}
                                        sx={{ minWidth: 150 }}
                                    >
                                        {COUNTRY_CODES.map((c) => (
                                            <MenuItem value={c.code} key={c.code}>{c.label}</MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField label="Phone No" type="tel" size="small" required fullWidth value={form.phoneNo} onChange={(e) => setForm({ ...form, phoneNo: e.target.value.replace(/\D/g, '') })} />
                                </div>

                                <FormControlLabel
                                    control={<Checkbox checked={form.isDefault} onChange={(e) => setForm({ ...form, isDefault: e.target.checked })} />}
                                    label="Make this my default address"
                                />
                                <div className="flex gap-3 justify-end">
                                    <button type="button" onClick={() => setShowForm(false)} className="text-sm px-4 py-2 border rounded hover:bg-gray-50">Cancel</button>
                                    <button type="submit" className="bg-primary-orange text-white text-sm font-medium px-4 py-2 rounded shadow hover:shadow-lg">
                                        {editingId ? "Save Changes" : "Save Address"}
                                    </button>
                                </div>
                            </form>
                        )}

                        {!loading && addresses.length === 0 && !showForm && (
                            <p className="text-sm text-gray-500">No saved addresses yet.</p>
                        )}

                        <div className="flex flex-col gap-3">
                            {addresses.map((addr) => (
                                <div key={addr._id} className={`border rounded p-4 flex justify-between items-start ${addr.isDefault ? "border-primary-blue" : ""}`}>
                                    <div>
                                        <p className="font-medium flex items-center gap-2">
                                            {addr.label}
                                            {addr.isDefault && <span className="text-xs bg-blue-100 text-primary-blue px-2 py-0.5 rounded-full">Default</span>}
                                        </p>
                                        <p className="text-sm text-gray-600">{addr.address}, {addr.area}, {addr.region}</p>
                                        <p className="text-sm text-gray-500">Phone: +{addr.phoneCountryCode || '852'} {addr.phoneNo}</p>
                                    </div>
                                    <div className="flex flex-col gap-1 items-end text-sm flex-shrink-0">
                                        <button onClick={() => startEdit(addr)} className="text-primary-blue hover:underline">Edit</button>
                                        <button onClick={() => deleteHandler(addr._id)} className="text-red-500 hover:underline">Delete</button>
                                        {!addr.isDefault && (
                                            <button onClick={() => makeDefaultHandler(addr)} className="text-gray-500 hover:underline">Set as Default</button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default ManageAddresses;
